import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export default function CartDrawer({ cart, setCart, open, onClose, onOrdered }) {
  const { t } = useLang();
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const totale = cart.reduce((s, i) => s + i.prezzo * i.qty, 0);
  const setQty = (id, delta) =>
    setCart((prev) => prev.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i)));
  const removeItem = (id) => setCart((prev) => prev.filter((i) => i.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setLoading(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        cliente_nome: nome,
        cliente_telefono: telefono,
        note,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
      });
      const lines = order.items.map((i) => `${i.qty}× ${i.nome} — ${eur(i.prezzo * i.qty)}`).join("\n");
      const text = encodeURIComponent(
        `Nuovo ordine Stylottica Ceglie #${order.id.slice(0, 6)}\n\nNome: ${nome}\nTelefono: ${telefono}\n\n${lines}\n\nTotale: ${eur(order.totale)}${note ? `\n\nNote: ${note}` : ""}`
      );
      window.open(`https://wa.me/393925218114?text=${text}`, "_blank");
      toast.success(t("cart_toast_ok"));
      setCart([]);
      setNome("");
      setTelefono("");
      setNote("");
      onClose();
      onOrdered?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("cart_toast_err"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="cart-overlay"
            className="fixed inset-0 bg-[#1A1B18]/40 z-[60]"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#FAF9F6] z-[70] flex flex-col border-l border-[#DCD7CC]"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#DCD7CC]">
              <p className="font-display text-xl font-medium flex items-center gap-2">
                <ShoppingBag size={18} className="text-[#A86946]" /> {t("cart_title")}
              </p>
              <button onClick={onClose} data-testid="cart-close" aria-label={t("cart_close")} className="p-2 hover:text-[#A86946] transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <p data-testid="cart-empty" className="text-sm text-[#1A1B18]/50 text-center py-16">
                  {t("cart_empty")}
                </p>
              ) : (
                <ul className="divide-y divide-[#DCD7CC]">
                  {cart.map((i, idx) => (
                    <li key={i.id} data-testid={`cart-item-${idx}`} className="py-4 flex gap-4">
                      <img
                        src={`${API}/files/${i.image_path}`}
                        alt={i.nome}
                        className="w-16 h-16 object-cover border border-[#DCD7CC]"
                      />
                      <div className="flex-1">
                        <p className="font-display font-medium">{i.nome}</p>
                        <p className="text-sm text-[#A86946] font-semibold mt-0.5">{eur(i.prezzo)}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <button onClick={() => setQty(i.id, -1)} data-testid={`cart-minus-${idx}`} className="border border-[#DCD7CC] p-1 hover:bg-[#1A1B18] hover:text-white transition-colors" aria-label="Diminuisci">
                            <Minus size={13} />
                          </button>
                          <span className="text-sm w-5 text-center" data-testid={`cart-qty-${idx}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, 1)} data-testid={`cart-plus-${idx}`} className="border border-[#DCD7CC] p-1 hover:bg-[#1A1B18] hover:text-white transition-colors" aria-label="Aumenta">
                            <Plus size={13} />
                          </button>
                          <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${idx}`} className="ml-auto text-red-700/60 hover:text-red-700 transition-colors" aria-label="Rimuovi">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {cart.length > 0 && (
              <form onSubmit={submit} data-testid="cart-form" className="border-t border-[#DCD7CC] px-6 py-5 space-y-4 bg-white/50">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#1A1B18]/60">{t("cart_total")}</span>
                  <span data-testid="cart-totale" className="font-display text-2xl font-semibold text-[#A86946]">{eur(totale)}</span>
                </div>
                <Input required placeholder={t("ph_name")} value={nome} onChange={(e) => setNome(e.target.value)} data-testid="cart-input-nome" className="rounded-none border-[#DCD7CC] bg-transparent h-11" />
                <Input required type="tel" placeholder={t("ph_phone").replace(" (facoltativo)", "").replace(" (optional)", "")} value={telefono} onChange={(e) => setTelefono(e.target.value)} data-testid="cart-input-telefono" className="rounded-none border-[#DCD7CC] bg-transparent h-11" />
                <Input placeholder={t("cart_ph_note")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cart-input-note" className="rounded-none border-[#DCD7CC] bg-transparent h-11" />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="cart-submit"
                  className="w-full rounded-full bg-[#25D366] text-white text-xs uppercase tracking-[0.15em] px-7 py-4 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? t("cart_sending") : t("cart_submit")}
                </button>
                <p className="text-[11px] text-[#1A1B18]/50 text-center">
                  {t("cart_note")}
                </p>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
